/* lib/api/integrations.js — сетевые вызовы интеграций пространства (под /api/integrations/).
 * Опц. workspaceId → X-Workspace-Id (скоуп на конкретный бизнес-ws без переключения активного,
 * как в restaurant.js). Возвращают сырой контракт apiFetch {ok,status,data}; throw — через unwrap. */
import { apiFetch } from './client.js';
import { fitsInGetUrl } from './urlBudget.js';

function wsHeaders(workspaceId) {
  if (workspaceId == null) return undefined;
  return { 'X-Workspace-Id': String(workspaceId) };
}

export const getIntegration = (workspaceId) =>
  apiFetch('/api/integrations/', { headers: wsHeaders(workspaceId) });

export const saveIntegration = (payload, workspaceId) =>
  apiFetch('/api/integrations/', { method: 'PUT', body: payload, headers: wsHeaders(workspaceId) });

export const deleteIntegration = (workspaceId) =>
  apiFetch('/api/integrations/', { method: 'DELETE', headers: wsHeaders(workspaceId) });

export const testIntegration = (workspaceId) =>
  apiFetch('/api/integrations/test/', { method: 'POST', headers: wsHeaders(workspaceId) });

// Права API-пользователя iiko по областям аналитики. GET — кэш; POST — свежая проба.
export const getIikoPermissions = (workspaceId) =>
  apiFetch('/api/integrations/permissions/', { headers: wsHeaders(workspaceId) });

export const checkIikoPermissions = (workspaceId) =>
  apiFetch('/api/integrations/permissions/', { method: 'POST', headers: wsHeaders(workspaceId) });

// ОПиУ (P&L) «Финансы» по факту: дерево счётов за месяц + Пр.мес/Пр.год.
export const getIikoPnl = (period, workspaceId) => {
  const qs = period ? ('?period=' + encodeURIComponent(period)) : '';
  return apiFetch('/api/integrations/pnl/' + qs, { headers: wsHeaders(workspaceId) });
};

// Ручные расходы ОПиУ за месяц (аренда/зарплата/прочие). period — YYYY-MM.
export const getPnlExpenses = (period, workspaceId) =>
  apiFetch('/api/integrations/pnl/expenses/?period=' + encodeURIComponent(period),
    { headers: wsHeaders(workspaceId) });

export const savePnlExpenses = (period, items, workspaceId) =>
  apiFetch('/api/integrations/pnl/expenses/', {
    method: 'PUT', body: { period, items }, headers: wsHeaders(workspaceId),
  });

// ABC-анализ блюд по факту: позиции за период + карточки A/B/C. from/to — YYYY-MM-DD (опц.);
// extra — {scope: 'selection', groups: [имя, …], categories: [имя, …], pay, discount}:
// буквы считаются относительно выборки групп и категорий (их пересечения).
//
// Транспорт выбирается по длине адреса, а не по вкусу: список групп в него не
// помещается (см. urlBudget.js — так экран и падал с 400 на 55 группах). Влезло —
// обычный GET, адрес читаем в логах; не влезло — POST с теми же параметрами телом.
// На бэкенде обе формы сходятся в один расчёт (`IikoAbcView._build`), поэтому ветки
// не могут разойтись в ответе. Категории ходят ровно так же, как группы.
export const getIikoAbc = (from, to, workspaceId, extra) => {
  const e = extra || {};
  const groups = Array.isArray(e.groups) ? e.groups.filter(Boolean) : [];
  const categories = Array.isArray(e.categories) ? e.categories.filter(Boolean) : [];
  const qs = [];
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  if (e.scope) qs.push('scope=' + encodeURIComponent(e.scope));
  // '||' — разделитель имён групп: в самих именах бывают запятые (iiko_abc._selected_groups)
  if (groups.length) qs.push('groups=' + encodeURIComponent(groups.join('||')));
  if (categories.length) qs.push('categories=' + encodeURIComponent(categories.join('||')));
  if (e.pay) qs.push('pay=' + encodeURIComponent(e.pay));
  if (e.discount) qs.push('discount=' + encodeURIComponent(e.discount));
  const path = '/api/integrations/abc/' + (qs.length ? '?' + qs.join('&') : '');
  if (fitsInGetUrl(path)) return apiFetch(path, { headers: wsHeaders(workspaceId) });
  return apiFetch('/api/integrations/abc/', {
    method: 'POST',
    body: { from, to, scope: e.scope, groups, categories, pay: e.pay, discount: e.discount },
    headers: wsHeaders(workspaceId),
  });
};

// «Ликвидность товаров» по остаткам: карточки складов + позиции. Без параметров (снимок).
export const getIikoLiquidity = (workspaceId) =>
  apiFetch('/api/integrations/liquidity/', { headers: wsHeaders(workspaceId) });

// Окно расхода «Ликвидности»: сохранить + немедленно пересинкать остатки/расход.
// → {saved, synced, window_days} (synced:false — iiko недоступен, применит ночной синк)
export const postLiquidityWindow = (days, workspaceId) =>
  apiFetch('/api/integrations/liquidity/window/', {
    method: 'POST', body: { window_days: days }, headers: wsHeaders(workspaceId),
  });

// Дашборд «Обзор»: доходы (факт/пред.период/прошлый год) + структура + каналы Ресторан/Доставка.
// from/to — YYYY-MM-DD (опц.; дефолт — последние 30 дней).
export const getIikoOverview = (from, to, workspaceId) => {
  const qs = [];
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  const q = qs.length ? ('?' + qs.join('&')) : '';
  return apiFetch('/api/integrations/overview/' + q, { headers: wsHeaders(workspaceId) });
};

// Отчёт «Производительность»: KPI по дню недели + почасовой график. weekday — 'all'|'0'..'6'.
export const getIikoPerformance = (from, to, weekday, workspaceId) => {
  const qs = [];
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  if (weekday) qs.push('weekday=' + encodeURIComponent(weekday));
  const q = qs.length ? ('?' + qs.join('&')) : '';
  return apiFetch('/api/integrations/performance/' + q, { headers: wsHeaders(workspaceId) });
};

// Блок «Столы» «Обзора» (спрос по столам, тепловая карта, исключения из гостевой аналитики).
// from/to — период «Обзора»; weekday — 'all' | '0,2,4' (Пн=0, как у «Производительности»).
// zones — выбранные залы: фильтр считает сервер (итоги и доли — по выбранным залам, как
// пул должностей «Персонала»); одним параметром через «||», стол без зала ('') — токен
// `__none__` (пустой токен в адресе не отличить от «залов не выбрано»); пустой выбор —
// параметра нет, все залы. На демо-стенде тот же путь apiFetch переписывает на
// /api/demo/iiko/tables/.
export const getIikoTables = (from, to, weekday, zones, workspaceId) => {
  const qs = [];
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  if (weekday) qs.push('weekday=' + encodeURIComponent(weekday));
  const z = (Array.isArray(zones) ? zones : []).map((t) => (t == null || t === '' ? '__none__' : String(t)));
  if (z.length) qs.push('zones=' + encodeURIComponent(z.join('||')));
  const q = qs.length ? ('?' + qs.join('&')) : '';
  return apiFetch('/api/integrations/tables/' + q, { headers: wsHeaders(workspaceId) });
};

// Группа сотрудников (staff-groups): ключ группы или 'all'. Пусто — параметра нет вовсе,
// сервер сам выберет группу по умолчанию (для карточки сотрудника — 'all').
const groupOf = (extra) => (extra && typeof extra.group === 'string' && extra.group ? extra.group : null);
const withGroup = (body, extra) => {
  const g = groupOf(extra);
  return g ? { ...body, group: g } : body;
};

// Отчёт «Персонал»: KPI + потери (скидки/возвраты/сторно) + таблица сотрудников выбранной
// группы. from/to — YYYY-MM-DD (опц.); extra.group — ключ группы | 'all' (опц.).
export const getIikoStaff = (from, to, workspaceId, extra) => {
  const qs = [];
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  const g = groupOf(extra);
  if (g) qs.push('group=' + encodeURIComponent(g));
  const q = qs.length ? ('?' + qs.join('&')) : '';
  return apiFetch('/api/integrations/staff/' + q, { headers: wsHeaders(workspaceId) });
};

// Отчёт «Закупки»: поставщики + товары по приходным накладным. from/to — YYYY-MM-DD (опц.);
// extra.categories — 'имя||имя' (верхнеуровневый фильтр, срезает весь отчёт).
export const getIikoPurchases = (from, to, workspaceId, extra) => {
  const qs = [];
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  if (extra && extra.categories) qs.push('categories=' + encodeURIComponent(extra.categories));
  const q = qs.length ? ('?' + qs.join('&')) : '';
  return apiFetch('/api/integrations/purchases/' + q, { headers: wsHeaders(workspaceId) });
};

// Отчёт «Инвентаризация»: матрица расхождений сличительных ведомостей
// 13 месяцев × дерево складских групп → товары. store — имя склада (опц. фильтр).
export const getIikoInventory = (store, workspaceId) => {
  const q = store ? ('?store=' + encodeURIComponent(store)) : '';
  return apiFetch('/api/integrations/inventory/' + q, { headers: wsHeaders(workspaceId) });
};

// Drill-down инвентаризаций: ведомости по товару (product_id; month YYYY-MM
// опционален — без него бэкенд отдаёт все 13 месяцев окна) ИЛИ группе (top
// [+ second [+ third]] с обязательным month; top '' = «Без группы» — бэкенд ждёт
// точное имя). `month` без значения в адрес НЕ кладётся: раньше уезжала строка
// «undefined», и бэкенд отвечал bad_month (панель товара, 15.09.2026).
export const getIikoInventoryDetail = ({ month, product_id, top, second, third, store }, workspaceId) => {
  const qs = [];
  if (month) qs.push('month=' + encodeURIComponent(month));
  if (product_id) {
    qs.push('product_id=' + encodeURIComponent(product_id));
  } else {
    qs.push('top=' + encodeURIComponent(top || ''));
    if (second != null) qs.push('second=' + encodeURIComponent(second));
    if (third != null) qs.push('third=' + encodeURIComponent(third));
  }
  if (store) qs.push('store=' + encodeURIComponent(store));
  return apiFetch('/api/integrations/inventory/detail/?' + qs.join('&'), { headers: wsHeaders(workspaceId) });
};

// Финансовые настройки пространства (вкладка «Настройки»): комиссии/пост.расходы/планы.
export const getFinanceSettings = (workspaceId) =>
  apiFetch('/api/integrations/settings/finance/', { headers: wsHeaders(workspaceId) });

// PUT — секционная замена: присланный ключ (commissions | recurring_expenses | plans |
// liquidity | abc | service) заменяет свой набор целиком, остальные секции не трогаются.
// service — {nonfiscal_pay_types, staff_discounts}: null = авто по маркерам, список = вручную.
// staff_groups — {assignments: {имя: подпись | null}}: СЛИЯНИЕ по имени (null снимает
// назначение), не замена; ответ — весь payload настроек.
// tables — {set: [{zone, table, excluded, article?}]}: столы вне гостевой аналитики,
// СЛИЯНИЕ по паре (зал, стол); ответ несёт полный список `tables.excluded`.
export const saveFinanceSettings = (payload, workspaceId) =>
  apiFetch('/api/integrations/settings/finance/', {
    method: 'PUT', body: payload, headers: wsHeaders(workspaceId),
  });

// Деталка «Продаж по категориям»: блюда внутри категории/группы. name — метка разреза.
export const getIikoCategoryDetail = ({ name, dim, from, to, pay, discount }, workspaceId) => {
  const qs = ['name=' + encodeURIComponent(name)];
  if (dim) qs.push('dim=' + encodeURIComponent(dim));
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  // деталька открывается из уже отфильтрованной таблицы — иначе суммы разойдутся
  if (pay) qs.push('pay=' + encodeURIComponent(pay));
  if (discount) qs.push('discount=' + encodeURIComponent(discount));
  return apiFetch('/api/integrations/categories/detail/?' + qs.join('&'), { headers: wsHeaders(workspaceId) });
};

// Деталка закупок: supplier=<имя> → товары поставщика; product=<id|имя> → поставщики товара.
export const getIikoPurchaseDetail = ({ supplier, product, from, to }, workspaceId) => {
  const qs = [];
  if (supplier) qs.push('supplier=' + encodeURIComponent(supplier));
  if (product) qs.push('product=' + encodeURIComponent(product));
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  return apiFetch('/api/integrations/purchases/detail/?' + qs.join('&'), { headers: wsHeaders(workspaceId) });
};

// Карточка сотрудника: KPI vs команда группы, категории/позиции, тренд, потери. waiter
// обязателен; extra.group — группа, из которой открыта карточка (нет — сервер берёт 'all').
export const getIikoStaffWaiter = (waiter, from, to, workspaceId, extra) => {
  const qs = ['waiter=' + encodeURIComponent(waiter)];
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  const g = groupOf(extra);
  if (g) qs.push('group=' + encodeURIComponent(g));
  return apiFetch('/api/integrations/staff/waiter/?' + qs.join('&'), { headers: wsHeaders(workspaceId) });
};

// AI-ревью сотрудника: код считает карточку, LLM пишет текст. Синхронный вызов (~10-30 с).
export const postIikoStaffWaiterReview = (waiter, from, to, workspaceId, extra) =>
  apiFetch('/api/integrations/staff/waiter/review/', {
    method: 'POST', body: withGroup({ waiter, from, to }, extra), headers: wsHeaders(workspaceId),
  });

// «Обернуть в обучающий скрипт»: инструктаж для сотрудника по карточке + ревью (2-й AI-запрос).
export const postIikoWaiterTraining = (waiter, from, to, review, workspaceId, extra) =>
  apiFetch('/api/integrations/staff/waiter/training/', {
    method: 'POST', body: withGroup({ waiter, from, to, review }, extra), headers: wsHeaders(workspaceId),
  });

// Отправить инструктаж участнику пространства: PDF на почту + Telegram (бесплатно).
export const postIikoWaiterTrainingSend = (payload, workspaceId) =>
  apiFetch('/api/integrations/staff/waiter/training/send/', {
    method: 'POST', body: payload, headers: wsHeaders(workspaceId),
  });

// AI-разбор вкладки аналитики → НОВЫЙ чат ассистента (1 AI-запрос квоты).
// body — {kind, from?, to?, period?, dim?, rows?, context?}; ответ — {chat_id, review, usage}.
export const postIikoReportReviewChat = (body, workspaceId) =>
  apiFetch('/api/integrations/report-review/chat/', {
    method: 'POST', body, headers: wsHeaders(workspaceId),
  });

// Отчёт «Категории»: микс продаж по категориям/группам. from/to — YYYY-MM-DD (опц.); dim — category|group.
export const getIikoCategories = (from, to, dim, workspaceId, extra) => {
  const qs = [];
  if (from) qs.push('from=' + encodeURIComponent(from));
  if (to) qs.push('to=' + encodeURIComponent(to));
  if (dim) qs.push('dim=' + encodeURIComponent(dim));
  if (extra && extra.pay) qs.push('pay=' + encodeURIComponent(extra.pay));
  if (extra && extra.discount) qs.push('discount=' + encodeURIComponent(extra.discount));
  const q = qs.length ? ('?' + qs.join('&')) : '';
  return apiFetch('/api/integrations/categories/' + q, { headers: wsHeaders(workspaceId) });
};

// Заявка «подключите мне r_keeper» из онбординга (прямой интеграции нет — заявка менеджеру:
// CRM-лид + best-effort AmoCRM). → {status:'ok'}; требует business-ws; rate-limit 5/ч
// на пользователя → 429 {code:'rate_limited'}. comment — опционален.
export const postRkeeperRequest = (comment, workspaceId) =>
  apiFetch('/api/integrations/rkeeper-request/', {
    method: 'POST', body: comment ? { comment } : {}, headers: wsHeaders(workspaceId),
  });

/* Отчёт «Движение товара»: приход и расход остатков по режимам за период.
   Все параметры опциональны, кроме окна: `kind` — режимы через '||', `stores`,
   `cats`, `reasons` — многозначные фильтры (тем же разделителем), `q` — поиск.

   ⚠️ ВЛЕЗАЕТ — GET, не влезает — POST тем же телом, как у ABC. Это не запас на
   будущее: статей списания у живого подключения 25, имена длинные («Coffee
   Calibration & Waste — Списание кофе на калибровку и промыв»), кириллица в
   адресе кодируется по шесть символов на букву — «выбрать все статьи» даёт
   строку запроса заметно длиннее лимита gunicorn (`limit_request_line` = 4094 Б),
   и 400 приходит ЕЩЁ ДО Django. Обе формы сходятся на бэкенде в один
   `build_movement`, поэтому ветки не могут разойтись в ответе. */
const movementQs = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v != null && v !== '') qs.set(k, v);
  });
  return qs.toString();
};

export const getIikoMovement = (params, workspaceId) => {
  const s = movementQs(params);
  const path = '/api/integrations/movement/' + (s ? '?' + s : '');
  if (fitsInGetUrl(path)) return apiFetch(path, { headers: wsHeaders(workspaceId) });
  return apiFetch('/api/integrations/movement/', {
    method: 'POST', body: params || {}, headers: wsHeaders(workspaceId),
  });
};

// Drill-down движения: документы за ячейкой (товар ИЛИ группа) одного режима.
// У реализации документов нет — ответ придёт с kind:'daily' и днями вместо актов.
export const getIikoMovementDetail = (params, workspaceId) => {
  const path = '/api/integrations/movement/detail/?' + movementQs(params);
  if (fitsInGetUrl(path)) return apiFetch(path, { headers: wsHeaders(workspaceId) });
  return apiFetch('/api/integrations/movement/detail/', {
    method: 'POST', body: params || {}, headers: wsHeaders(workspaceId),
  });
};
